import React from 'react';
import classNames from 'classnames';

import {
  LandingPageAuthAwareLink,
  useLandingViewerRole,
} from '../../../LandingPage/LandingPageCta';
import { useConfiguration } from '../../../../context/configurationContext';
import { getServiceListingTypes, serviceSearchQuery } from '../../../../util/serviceSearch';
import SectionContainer from '../SectionContainer';

import css from './SectionClientsSpecialists.module.css';

// Static content. CTA hrefs match the current homepage landing-page.json targets.
// `cards` is the order where the four cards sit in a row (the two main actions meet in the middle);
// `stackedCardKeys` is the order where the cards are stacked (the panel's main action first, T14).
// `accent` marks the coral button, one per panel, the same for every viewer (D28 amended by T20, D31).
const AUDIENCE_GROUPS = [
  {
    eyebrow: 'DELOWO',
    panelTitle: 'Для клиентов',
    stackedCardKeys: ['findSpecialist', 'createTask'],
    cards: [
      {
        key: 'createTask',
        heading: 'Создать задачу',
        body:
          'Опишите задачу, а специалисты сами предложат свои услуги. Выберите специалиста по рейтингу и отзывам других клиентов',
        ctaLabel: 'Создать задачу',
        // the task contract, absolute like every other «Создать задачу» link (D29)
        ctaHref: '/l/draft/00000000-0000-0000-0000-000000000000/new/details?type=tasks',
      },
      {
        key: 'findSpecialist',
        accent: true,
        heading: 'Найти специалиста',
        body:
          'Ищите проверенных русскоязычных специалистов в Европе, находите нужные услуги на карте и общайтесь напрямую',
        // the button names the destination (the whole service catalogue), the heading the client's
        // goal (D4, T15)
        ctaLabel: 'Все услуги',
        // the unfiltered service search, built from the runtime listing types (D22, T15)
        isServiceSearch: true,
      },
    ],
  },
  {
    eyebrow: 'DELOWO',
    panelTitle: 'Для специалистов',
    cards: [
      {
        key: 'createService',
        accent: true,
        heading: 'Создать услугу',
        body:
          'Расширяйте свою аудиторию, получайте заказы и стройте доверительные отношения с клиентами в Европе',
        ctaLabel: 'Создать услугу',
        ctaHref: '/l/new',
      },
      {
        key: 'findTask',
        heading: 'Найти задачу',
        body: 'Находите опубликованные задачи и зарабатывайте на том, что умеете',
        ctaLabel: 'Найти задачу',
        ctaHref: '/s?pub_listingType=tasks',
      },
    ],
  },
];

const stackedCards = group =>
  group.stackedCardKeys
    ? group.stackedCardKeys.map(key => group.cards.find(card => card.key === key))
    : group.cards;

// T14: the order differs between layouts, and Tab / screen readers follow the DOM, not CSS `order`.
// So an item of a two-item list that moves is rendered once per layout ('stacked' / 'wide'); the CSS
// shows one copy per band, and display: none keeps the other out of the tab order and the a11y tree.
const forBothLayouts = (wide, stacked) =>
  wide[0] === stacked[0]
    ? wide.map(item => ({ item, only: null }))
    : [
        { item: stacked[0], only: 'stacked' },
        { item: wide[0], only: null },
        { item: wide[1], only: 'wide' },
      ];

/**
 * Custom homepage section: two-panel audience grid (customers / specialists).
 *
 * Accepts the standard PageBuilder section props plus:
 *   onCustomerServiceClick — forwarded from LandingPage to the role-notice modal trigger.
 *   When wired via LandingPage.js options, pass as props.options.onCustomerServiceClick.
 *   When used standalone (e.g. tests), pass directly.
 */
const SectionClientsSpecialists = props => {
  const { sectionId, className, onCustomerServiceClick: directProp, options } = props;

  // Support two call sites:
  // 1. Direct prop (standalone / test usage)
  // 2. Via PageBuilder options (production wiring through LandingPage.js)
  const onCustomerServiceClick = directProp ?? options?.onCustomerServiceClick;
  const role = useLandingViewerRole();
  const serviceSearchHref = `/s${serviceSearchQuery(getServiceListingTypes(useConfiguration()))}`;
  // Where the panels are stacked, a provider sees their own panel first (T14).
  const stackedGroups = role === 'provider' ? [...AUDIENCE_GROUPS].reverse() : AUDIENCE_GROUPS;

  return (
    <SectionContainer id={sectionId} className={classNames(className, css.sectionDark)}>
      <div className={css.outer}>
        <div className={css.audienceGrid}>
          {forBothLayouts(AUDIENCE_GROUPS, stackedGroups).map(
            ({ item: group, only: panelOnly }) => {
              const otherGroup = AUDIENCE_GROUPS.find(g => g !== group);
              return (
                <article
                  key={`${group.panelTitle}-${panelOnly || 'both'}`}
                  className={classNames(css.audiencePanel, {
                    [css.panelStackedOnly]: panelOnly === 'stacked',
                    [css.panelWideOnly]: panelOnly === 'wide',
                  })}
                >
                  <p className={classNames('delowo-text--panel-eyebrow', css.eyebrow)}>
                    {group.eyebrow}
                  </p>
                  <h2 className={classNames('delowo-text--panel-title', css.panelTitle)}>
                    {group.panelTitle}
                  </h2>
                  <div className={css.cardGrid}>
                    {forBothLayouts(group.cards, stackedCards(group)).map(
                      ({ item: card, only: cardOnly }) => (
                        <div
                          key={`${card.key}-${cardOnly || 'both'}`}
                          className={classNames(css.card, {
                            [css.cardStackedOnly]: cardOnly === 'stacked',
                            [css.cardWideOnly]: cardOnly === 'wide',
                          })}
                        >
                          <h3 className="delowo-text--card-title">{card.heading}</h3>
                          <p className="delowo-text--card-body">{card.body}</p>
                          {/* rootClassName, not className: the PageBuilder Link's own text-link
                            class must not sit on the button (T20, D31) */}
                          <LandingPageAuthAwareLink
                            href={card.isServiceSearch ? serviceSearchHref : card.ctaHref}
                            rootClassName={classNames(css.ctaButton, {
                              [css.ctaAccent]: card.accent,
                            })}
                            onCustomerServiceClick={onCustomerServiceClick}
                          >
                            {/* Where the buttons are sized to their content, every button in a row is
                              as wide as the row's longest label: the hidden ::before / ::after carry
                              the labels of the row mates — the other card of the panel (768–1023),
                              and the card at the same stacked position in the other panel (1280–1400,
                              a stacked band). Matched by key, not by DOM index (copies, T14). */}
                            <span
                              className={css.ctaLabel}
                              data-row-panel={group.cards.find(c => c.key !== card.key)?.ctaLabel}
                              data-row-cross={
                                stackedCards(otherGroup)[
                                  stackedCards(group).findIndex(c => c.key === card.key)
                                ]?.ctaLabel
                              }
                            >
                              <span className={css.ctaText}>{card.ctaLabel}</span>
                            </span>
                          </LandingPageAuthAwareLink>
                        </div>
                      )
                    )}
                  </div>
                </article>
              );
            }
          )}
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionClientsSpecialists;
